import { useCallback, useRef } from "react";

// DOM 信息收集相关类型定义
export interface DOMElementInfo {
    id: string;
    bounds: DOMRect;
    element: HTMLElement;
}

export interface DOMInfo {
    type: "single" | "multiple" | "none";
    elements: DOMElementInfo[];
}

/**
 * DOM 信息收集 Hook
 * 用于收集卡片中按钮的 DOM 信息,支持单个或多个按钮
 *
 * @returns {Object} 包含 registerButton 和 collectDOMInfo 方法
 * - registerButton: 注册需要收集的按钮元素
 * - collectDOMInfo: 收集所有已注册按钮的 DOM 信息
 *
 * @example
 * ```tsx
 * const { registerButton, collectDOMInfo } = useCollectDOMInfo();
 *
 * // 在组件中注册按钮
 * <button ref={(el) => registerButton('yes-button', el)}>Yes</button>
 *
 * // 暴露给父组件
 * useImperativeHandle(ref, () => ({ collectDOMInfo }));
 * ```
 */
export function useCollectDOMInfo() {
    // 使用 Map 存储按钮引用,key 为按钮 id,value 为 HTMLElement
    const buttonRefs = useRef<Map<string, HTMLElement>>( new Map() );

    // 缓存机制
    const cachedDOMInfo = useRef<DOMInfo | null>( null );
    const lastCollectTime = useRef<number>( 0 );

    /**
     * 注册按钮元素
     * @param id - 按钮的唯一标识符
     * @param element - 按钮的 DOM 元素,如果为 null 则删除该按钮
     */
    const registerButton = useCallback( ( id: string, element: HTMLElement | null ) => {
        if ( element ) {
            buttonRefs.current.set( id, element );
            // 清除缓存,因为按钮列表发生了变化
            cachedDOMInfo.current = null;
        } else {
            buttonRefs.current.delete( id );
            // 清除缓存,因为按钮列表发生了变化
            cachedDOMInfo.current = null;
        }
    }, [] );

    /**
     * 收集所有已注册按钮的 DOM 信息
     * @param forceRefresh - 是否强制刷新缓存
     * @returns DOM 信息对象
     */
    const collectDOMInfo = useCallback( ( forceRefresh = false ): DOMInfo => {
        const now = Date.now();
        const CACHE_DURATION = 100; // 100ms 缓存时间

        // 如果有缓存且在有效期内,直接返回缓存
        if ( !forceRefresh && cachedDOMInfo.current && ( now - lastCollectTime.current ) < CACHE_DURATION ) {
            return cachedDOMInfo.current;
        }

        try {
            const elements: DOMElementInfo[] = [];

            // 遍历所有已注册的按钮
            buttonRefs.current.forEach( ( element, id ) => {
                try {
                    // 检查元素是否仍然在 DOM 中
                    if ( element && element.isConnected ) {
                        const bounds = element.getBoundingClientRect();
                        elements.push( {
                            id,
                            bounds,
                            element,
                        } );
                    }
                } catch ( error ) {
                    console.warn( `[useCollectDOMInfo] Error processing button ${id}:`, error );
                }
            } );

            // 根据收集到的元素数量确定类型
            const result: DOMInfo = {
                type: elements.length === 0
                    ? "none"
                    : elements.length === 1
                    ? "single"
                    : "multiple",
                elements,
            };

            // 缓存结果
            cachedDOMInfo.current = result;
            lastCollectTime.current = now;

            return result;
        } catch ( error ) {
            console.error( "[useCollectDOMInfo] Error collecting DOM info:", error );

            const errorResult: DOMInfo = {
                type: "none",
                elements: [],
            };

            // 缓存错误结果
            cachedDOMInfo.current = errorResult;
            lastCollectTime.current = now;

            return errorResult;
        }
    }, [] );

    return {
        registerButton,
        collectDOMInfo,
    };
}
